import styled from "@emotion/styled";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import openLink from "src/assets/images/open-link.png";
import { i18n, navigate, showMessage, useAcceptTermsAndSetContractorAndInsuredPersons, useForm, useGeneralSoContext, useGetSo, useLoader } from "src/common";
import { focusFirstInvalid } from "src/common/style/a11y.style";
import { useGetOffer } from "src/common/apiGraphql/queries/useGetOffer.query";
import { ContractorInput, InsuredPersonsInput, SoTravelContractorData, TravelDataType } from "src/common/apiGraphql/types/types";
import {
  Button,
  CheckboxInput,
  ContainerComponent,
  Flex,
  Loader,
  Stepper,
  TermsAndConditionsModal,
  Text
} from "src/components";
import { Card, OfferDetailsCard } from "src/components/cards";
import {
  ContractorAndInsuredPersonsForm,
  ContractorAndInsuredPersonsFormValues,
  ContractorFormValues,
  InsuredPersonsFormValues
} from "src/components/forms/contractorAndInsuredPersons.form";
import { colors } from "src/theme";
import { AppRoutes } from ".";
import { Helmet } from "react-helmet-async";


const ButtonStyle = styled(Button)({
  marginTop: 24
});

const TermsHeading = styled.h2({
  margin: 0,
  fontSize: 'inherit',
  fontWeight: 'inherit',
});

const OpenLinkStyle = styled.img({
  width: 16,
  height: 16,
});

const SelectedOfferPage = () => {
  const { formRef, getValues, validateForm } = useForm();
  const { so, refetch } = useGetSo();
  const { setSoId, setOfferId, setSoInitialData, offerId, soId } = useGeneralSoContext();
  const { offer, refetch: refetchOffer } = useGetOffer();
  const [acceptTermsAndSetContractorAndInsuredPersons] = useAcceptTermsAndSetContractorAndInsuredPersons();
  const { soId: soIdFromUrl, offerId: offerIdFromUrl } = useParams();
  const { startLoader, stopLoader } = useLoader();
  const [maxAge, setMaxAge] = useState<number | null>(null);
  const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(!!offer?.productTypeSpecific?.termsAcceptedAt || false);
  const [isExtraTermsAccepted, setIsExtraTermsAccepted] = useState<boolean>(false);
  const [termsModalOpen, setTermsModalOpen] = useState<boolean>(false);
  const [termsErrors, setTermsErrors] = useState<{ terms?: string; extraTerms?: string }>({});

  useEffect(() => {
    if (soIdFromUrl) {
      setSoId(soIdFromUrl);
    }
    if (offerIdFromUrl) {
      setOfferId(offerIdFromUrl);
    }
  }, [soIdFromUrl, offerIdFromUrl]);


  useEffect(() => {
    if (!soId || !offerId) {
      navigate(AppRoutes.WEBSHOP.STEP_1);
      return;
    }
    if (so) {
      refetch();
    }
    if (offer) {
      refetchOffer();
    }
  }, []);

  useEffect(() => {
    if (offer) {
      const brandCode = offer.product.brand.code;
      if (brandCode === 'uniqa') {
        setMaxAge(70);
      } else if (brandCode === 'sava') {
        setMaxAge(74);
      } else {
        setMaxAge(80);
      }
    }
  }, [offer]);

  useEffect(() => {
    if (so) {
      setSoInitialData(so.productTypeSpecific?.travelData as TravelDataType);
    }
    if (so?.productTypeSpecific?.travelData?.insuranceStartDate) {
      const startDate = dayjs(so.productTypeSpecific.travelData.insuranceStartDate);
      const today = dayjs().startOf('day');
      if (startDate.isBefore(today) || startDate.isSame(today, 'day')) {
        navigate(AppRoutes.WEBSHOP.STEP_1);
      }
    }
  }, [so]);

  useEffect(() => {
    if (offer) {
      setIsTermsAccepted(offer.productTypeSpecific?.termsAcceptedAt === 'true' || false);
    }
  }, [offer]);

  const contractorModelToForm = (contractor?: SoTravelContractorData): ContractorFormValues => {
    return {
      firstName: contractor?.firstName || '',
      lastName: contractor?.lastName || '',
      birthDate: contractor?.birthDate || '',
      oib: contractor?.oib || '',
      city: contractor?.city || '',
      cityKompareId: contractor?.cityKompareId || '',
      street: contractor?.street || '',
      streetKompareId: contractor?.streetKompareId || '',
      zipCode: contractor?.zipCode || 0,
      houseNumber: contractor?.houseNumber || '',
      email: contractor?.email || so?.email || '',
      contractorIsPassenger: contractor?.contractorIsPassenger ?? true,
    };
  };

  const contractorFormToModel = (values: ContractorFormValues): ContractorInput => {
    return {
      firstName: values.firstName || '',
      lastName: values.lastName || '',
      birthDate: values.birthDate || '',
      oib: values.oib || '',
      city: values.city || '',
      cityKompareId: values.cityKompareId || '',
      street: values.street || '',
      streetKompareId: values.streetKompareId || '',
      zipCode: values.zipCode || 0,
      houseNumber: values.houseNumber || '',
      email: values.email || '',
      contractorIsPassenger: values.contractorIsPassenger ?? false,
    };
  };

  const isSavaBrand = offer?.product?.brand?.code === 'sava';

  const insuredPersonsFormToModel = (values: InsuredPersonsFormValues): Array<InsuredPersonsInput> => {
    return values.insuredPersons ? values.insuredPersons.map((person) => {
      return {
        firstName: person.firstName || '',
        lastName: person.lastName || '',
        birthDate: person.birthDate || '',
        child: person.child || false,
        ...(isSavaBrand && person.oib ? { oib: person.oib } : {}),
      };
    }) : [];
  };

  const insuredPersonsModelToForm = (): InsuredPersonsFormValues => {
    return {
      insuredPersons: so?.productTypeSpecific?.insuredPersons ? so?.productTypeSpecific?.insuredPersons?.map((person) => {
        return {
          firstName: person.firstName || '',
          lastName: person.lastName || '',
          birthDate: person.birthDate || '',
          child: false,
          oib: person.oib || '',
        };
      }) : []
    };
  };

  const handleFormSubmit = async () => {
    // Validate form and consents together so every missing field gets its own message
    const isValid = validateForm();
    const newTermsErrors = {
      terms: isTermsAccepted ? undefined : i18n.t('pages.selectedOffer.termsRequired'),
      extraTerms: isExtraTermsAccepted ? undefined : i18n.t('pages.selectedOffer.extraTermsRequired'),
    };
    setTermsErrors(newTermsErrors);

    if (!isValid || newTermsErrors.terms || newTermsErrors.extraTerms) {
      focusFirstInvalid();
      return;
    }

    // Get current values from form
    const formValues = getValues() as ContractorAndInsuredPersonsFormValues;

    if (!formValues || !formValues.contractor || (!formValues.insuredPersons && (so?.productTypeSpecific?.travelData?.numOfInsuredPersons || 0) > 1)) {
      return;
    }

    startLoader();

    const completeSubmissionData = {
      contractor: contractorFormToModel(formValues.contractor),
      insuredPersons: insuredPersonsFormToModel(formValues.insuredPersons),
    };

    await acceptTermsAndSetContractorAndInsuredPersons(completeSubmissionData).then(() => {
      showMessage({ message: i18n.t('common.messages.acceptedTermsAndConditions'), type: 'success', duration: 6500, fixed: true });
      if (offer?.product.productTypeSpecific?.offline) {
        navigate(AppRoutes.WEBSHOP.CHECKOUT_SUCCESS_OFFLINE);
      } else {
        navigate(AppRoutes.WEBSHOP.CHECKOUT);
      }
    }).catch(() => {
      showMessage({
        type: 'error',
        message: i18n.t('common.messages.error'),
        duration: 5000,
        fixed: true
      });
    }).finally(() => {
      stopLoader();
    });
  };

  return (
    <ContainerComponent>
      <Helmet>
        <title>Vaša ponuda | Putno osiguranje - kompare.hr</title>
      </Helmet>
      <Flex vertical gap={24}>
        <Stepper currentStep={4} totalSteps={5} />
        {(offer && so) && <Flex vertical gap={24}>
          <Flex vertical>
            <Text as="h1" type="xxLarge" bold>
              {i18n.t(`pages.selectedOffer.title`)}
            </Text>
            <Text type="small" color={colors.text4}>
              {i18n.t("pages.selectedOffer.subtitle")}
            </Text>
          </Flex>
          <OfferDetailsCard offer={offer} travelData={so.productTypeSpecific?.travelData as TravelDataType} />
          {maxAge && <ContractorAndInsuredPersonsForm
            maxAge={maxAge}
            ref={formRef}
            contractorData={contractorModelToForm(so.productTypeSpecific?.contractor as SoTravelContractorData)}
            insuredPersonsData={insuredPersonsModelToForm()}
            numOfInsuredAdults={(so.productTypeSpecific?.travelData?.numOfInsuredAdults ?? 0)}
            numOfInsuredChildren={(so.productTypeSpecific?.travelData?.numOfInsuredChildren ?? 0)}
            showInsuredPersonOib={isSavaBrand}
          />}
          <Card>
            <Flex vertical gap={12}>
              <TermsHeading>
                <Button type="button" btnType="textPrimary" text={i18n.t("pages.selectedOffer.terms")} onClick={() => setTermsModalOpen(true)}>
                  <OpenLinkStyle src={openLink} alt="" />
                </Button>
              </TermsHeading>
              <CheckboxInput
                id="termsAccepted"
                value={isTermsAccepted}
                onChange={() => {
                  setIsTermsAccepted(!isTermsAccepted);
                  setTermsErrors(prev => ({ ...prev, terms: undefined }));
                }}
                left={true}
                required={true}
                error={termsErrors.terms}
                labelComponent={<Text as="span" type="small" dangerouslySetInnerHTML={{ __html: i18n.t("pages.selectedOffer.termsDescription") }} />}
              />
              <CheckboxInput
                id="extraTermsAccepted"
                value={isExtraTermsAccepted}
                onChange={() => {
                  setIsExtraTermsAccepted(!isExtraTermsAccepted);
                  setTermsErrors(prev => ({ ...prev, extraTerms: undefined }));
                }}
                left={true}
                required={true}
                error={termsErrors.extraTerms}
                labelComponent={<Text as="span" type="small" dangerouslySetInnerHTML={{ __html: i18n.t("pages.selectedOffer.extraTerms") }} />}
              />
              <ButtonStyle btnType="primary" text={i18n.t("components.buttons.submit")} onClick={handleFormSubmit} />
            </Flex>
          </Card>
        </Flex>}
        {(!offer || !so) && <Loader />}
        {termsModalOpen && <TermsAndConditionsModal open={termsModalOpen} onClose={() => setTermsModalOpen(false)} />}
      </Flex>
    </ContainerComponent>
  );
};

export default SelectedOfferPage;